import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  DEFAULT_MAX_THREAD_COLORS,
  constrainMappingToMaxColors,
  type PaletteEntry,
} from '@designdeck/shared';

/**
 * Working state for the embroidery thread modal.
 *
 * Deliberately NOT the home of the confirmed mapping. Confirmation is object
 * state — a `confirmedMapping` custom property on the Fabric object — because
 * `clearEmbroidery()` runs on every switch away from an embroidery product. A
 * store-held confirmation would silently unblock publishing after a product
 * switch, and would drift from the canvas on undo. What lives here is the
 * in-flight draft plus everything the modal needs to render.
 */

export interface EmbroiderySource {
  /** Blob URL of the uploaded file — session-scoped, gone after a reload. */
  objectUrl: string | null;
  /** Storage key of the stored source; survives a reload via the canvas object. */
  embFileKey: string | null;
}

interface EmbroideryStore {
  /** Whether the focused product is decorated by embroidery. */
  isEmbroideryProduct: boolean;
  setEmbroideryProduct: (value: boolean) => void;

  /**
   * Whether an embroidery motif sits on the canvas. Driven from the canvas
   * rather than from the upload call, so it survives a product switch.
   */
  isEmbroideryActive: boolean;
  setEmbroideryActive: (value: boolean) => void;

  /** Dominant design colors of the current motif. */
  palette: PaletteEntry[];

  source: EmbroiderySource;

  /** Design hex -> thread id, as edited in the modal. */
  draftMapping: Record<string, string>;
  assignThread: (designHex: string, threadId: string) => void;

  /**
   * Hexes the automatic narrowing moved off their suggested thread. The modal
   * marks these rows — without it the seller confirms merges they never made.
   */
  autoMergedHexes: string[];

  /** Draft as it stood when the modal opened, restored on cancel. */
  draftSnapshot: {
    mapping: Record<string, string>;
    autoMergedHexes: string[];
  } | null;

  /** Lowest ceiling across all active embroidery products. */
  maxColors: number;
  setMaxColors: (value: number) => void;

  /**
   * DecorationMethod.minDpi of the active method. Sizes the render that ships,
   * since the stitch optics are the production artwork rather than a preview.
   */
  minDpi: number | null;
  setMinDpi: (value: number | null) => void;

  isModalOpen: boolean;
  openModal: () => void;
  /** Closes and discards the edits made since it opened. */
  closeModal: () => void;
  /**
   * Closes and keeps the edits — for Apply. Closing through `closeModal` after
   * a successful apply rolled the draft back to the snapshot, so the next open
   * from memory showed the mapping the seller had just replaced.
   */
  commitDraft: () => void;

  /**
   * Seed the modal for a motif, narrowing the suggestion to the current ceiling
   * on the way in. Revokes a previously held blob URL.
   *
   * Narrowing lives here rather than at the call sites because both of them —
   * a fresh upload and a reopen after a reload — need it against the same
   * `maxColors`, which only the store knows.
   */
  startMapping: (input: {
    palette: PaletteEntry[];
    /** Mapping already recorded on the motif, if any. */
    existingMapping?: Record<string, string>;
    objectUrl?: string | null;
    embFileKey?: string | null;
    openModal?: boolean;
  }) => void;

  isProcessing: boolean;
  setProcessing: (value: boolean) => void;

  clearEmbroidery: () => void;
}

const emptySource: EmbroiderySource = { objectUrl: null, embFileKey: null };

function revoke(url: string | null) {
  if (url) URL.revokeObjectURL(url);
}

export const useEmbroideryStore = create<EmbroideryStore>()(
  devtools(
    (set) => ({
      isEmbroideryProduct: false,
      isEmbroideryActive: false,
      palette: [],
      source: emptySource,
      draftMapping: {},
      autoMergedHexes: [],
      draftSnapshot: null,
      maxColors: DEFAULT_MAX_THREAD_COLORS,
      minDpi: null,
      isModalOpen: false,
      isProcessing: false,

      setEmbroideryProduct: (value) => set({ isEmbroideryProduct: value }),
      setEmbroideryActive: (value) => set({ isEmbroideryActive: value }),
      setMaxColors: (value) =>
        set({ maxColors: Math.max(1, Math.floor(value)) }),
      setMinDpi: (value) => set({ minDpi: value }),

      assignThread: (designHex, threadId) =>
        set((state) => ({
          draftMapping: { ...state.draftMapping, [designHex]: threadId },
          // A hand-picked thread is the seller's own choice; drop the marker.
          autoMergedHexes: state.autoMergedHexes.filter(
            (hex) => hex !== designHex
          ),
        })),

      openModal: () =>
        set((state) => ({
          isModalOpen: true,
          // Snapshot so Cancel can put the mapping back the way it was. Without
          // it an abandoned edit survived in the store and the next open showed
          // choices the seller had already walked away from.
          draftSnapshot: {
            mapping: state.draftMapping,
            autoMergedHexes: state.autoMergedHexes,
          },
        })),
      closeModal: () =>
        set((state) => ({
          isModalOpen: false,
          draftMapping: state.draftSnapshot?.mapping ?? state.draftMapping,
          autoMergedHexes:
            state.draftSnapshot?.autoMergedHexes ?? state.autoMergedHexes,
          draftSnapshot: null,
        })),
      commitDraft: () => set({ isModalOpen: false, draftSnapshot: null }),

      startMapping: ({
        palette,
        existingMapping = {},
        objectUrl = null,
        embFileKey = null,
        openModal = true,
      }) =>
        set((state) => {
          if (state.source.objectUrl && state.source.objectUrl !== objectUrl) {
            revoke(state.source.objectUrl);
          }
          const constrained = constrainMappingToMaxColors(
            palette,
            existingMapping,
            state.maxColors
          );
          return {
            palette,
            source: { objectUrl, embFileKey },
            draftMapping: constrained.mapping,
            autoMergedHexes: constrained.mergedHexes,
            // Seeding is the new baseline; cancel returns here, not to whatever
            // the previous motif left behind.
            draftSnapshot: {
              mapping: constrained.mapping,
              autoMergedHexes: constrained.mergedHexes,
            },
            isModalOpen: openModal,
          };
        }),

      setProcessing: (value) => set({ isProcessing: value }),

      clearEmbroidery: () =>
        set((state) => {
          revoke(state.source.objectUrl);
          return {
            isEmbroideryActive: false,
            palette: [],
            source: emptySource,
            draftMapping: {},
            autoMergedHexes: [],
            draftSnapshot: null,
            isModalOpen: false,
            isProcessing: false,
          };
        }),
    }),
    { name: 'embroidery-store' }
  )
);
